import type { AppProps } from 'next/app'
import type { ReactPageProps } from '@/cms/rendering-strategy/types'
import type { SlugStaticProps } from '@/cms/types'
import { OptimizelyProvider } from '@optimizely/react-sdk'
import * as tokens from '@sunwebgroup/design-tokens/sunweb-sun'
import { UdsTokensProvider } from '@sunwebgroup/uds-ui/utils'
import { GlobalStyles } from '@sunwebgroup/ui-components'
import { BreakpointProvider } from '@sunwebgroup/ui-components/uds'
import { useRouter } from 'next/navigation'
import * as React from 'react'
import { RawIntlProvider } from 'react-intl'
import { ThemeProvider } from 'styled-components'
import { PageFieldsContext } from '@/cms/fields'
import { useInterceptNextDataHref } from '@/cms/hooks/useInterceptNextDataHref'
import renderingStrategyAbstractFactory from '@/cms/rendering-strategy/renderingStrategyAbstractFactory'
import { relativePastTimeResolver } from '@/intl/relativePastTimeResolver'
import { Custom500 } from '@/pages/500'
// This should be the first import
import { loadPolyfills, polyfillIntlPlural } from '@/polyfills'
import { reactHybridPageRegistry, reactPageRegistry } from '@/react-pages'
import { EmergencyBanner, SkipLinks } from '@/scopes/common/components'
import { CatalogueDetailsProvider } from '@/scopes/common/components/CatalogueDetailsContext'
import { useInitLegacyDesignSystemAfterRehydration } from '@/scopes/common/components/hooks/useInitLegacyDesignSystemAfterRehydration'
import { BreakpointProvider as LegacyBreakpointProvider } from '@/scopes/common/components/utils/breakpoints'
import { AbTestContext } from '@/scopes/common/infrastructure/ab-testing/edge-function/context'
import { optimizelyClient, useOptimizely } from '@/scopes/common/infrastructure/ab-testing/optimizely/useOptimizely'
import ga4analyticsClient from '@/scopes/common/infrastructure/analytics/ga4analyticsClient'
import { ConfigurationContext } from '@/scopes/common/infrastructure/config/ConfigProvider'
import { CommonDependenciesProvider } from '@/scopes/common/infrastructure/dependencies/common'
import featureFlags from '@/scopes/common/infrastructure/feature-flags'
import { NewRelicErrorBoundary } from '@/scopes/common/infrastructure/logging/NewRelicErrorBoundary'
import { createNewRelicLogger } from '@/scopes/common/infrastructure/logging/newRelicLogger'
import { createPriceDetailsApiClient } from '@/scopes/common/infrastructure/price-details/api/priceDetailsApiClient'
import { createQueryStringClient } from '@/scopes/common/infrastructure/query-strings/queryStringClient'
import { userAuthenticationClient } from '@/scopes/common/infrastructure/user'
import '@brand-tokens'
import '@adyen/adyen-web/styles/adyen.css'
import '@sunwebgroup/uds-ui/z-index.css'

loadPolyfills()

function ReactPage({ children, intl, locale, cmsFields, config, tokens: udsTokens, abTest }: ReactPageProps) {
  const localeForPolyfills = locale

  const { userId } = useOptimizely(config ?? null)
  const nextRouter = useRouter()

  polyfillIntlPlural(localeForPolyfills)

  const { isEnabled: isAbTestEnabled, isBVariant: isAbTestBVariant } = abTest
  const abTestValue = React.useMemo(
    () => ({ isEnabled: isAbTestEnabled, isBVariant: isAbTestBVariant }),
    [isAbTestEnabled, isAbTestBVariant],
  )

  return (
    <CommonDependenciesProvider
      dependencies={{
        env: {
          MEDIA_SERVER_BASE_URL: process.env.NEXT_PUBLIC_MEDIA_SERVER_BASE_URL,
          NEWRELIC_SCRIPT_NAME: process.env.NEXT_PUBLIC_NEWRELIC_SCRIPT_NAME,
          RECAPTCHA_SITE_KEY: process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY,
        },
        relativePastTimeResolver,
        analyticsClient: ga4analyticsClient,
        featureFlagsStore: featureFlags.store,
        priceDetailsApiClient: createPriceDetailsApiClient({ contextItemId: cmsFields.contextItemId }),
        queryStringClient: createQueryStringClient({ frameworkRouter: nextRouter }),
        userAuthenticationClient,
        logger: createNewRelicLogger({ scope: 'react-page' }),
        // For now we add this api client here, as this is a common place for both search page and acco pages
      }}
    >
      <ConfigurationContext value={config ?? null}>
        <AbTestContext value={abTestValue}>
          <PageFieldsContext value={cmsFields}>
            <OptimizelyProvider optimizely={optimizelyClient} user={{ id: userId }}>
              <RawIntlProvider value={intl}>
                <ThemeProvider theme={{ tokens, udsTokens, themeName: 'sunweb-sun' }}>
                  <UdsTokensProvider tokens={udsTokens}>
                    <LegacyBreakpointProvider>
                      <BreakpointProvider>
                        <NewRelicErrorBoundary scope="react-page" fallback={<Custom500 brand={config?.brandName ?? 'sunweb'} />}>
                          <CatalogueDetailsProvider>
                            <EmergencyBanner />
                            <SkipLinks />
                            <GlobalStyles />
                            {children}
                          </CatalogueDetailsProvider>
                        </NewRelicErrorBoundary>
                      </BreakpointProvider>
                    </LegacyBreakpointProvider>
                  </UdsTokensProvider>
                </ThemeProvider>
              </RawIntlProvider>
            </OptimizelyProvider>
          </PageFieldsContext>
        </AbTestContext>
      </ConfigurationContext>
    </CommonDependenciesProvider>
  )
}

export default function App(appPageProps: AppProps<SlugStaticProps>) {
  const { Component, pageProps, router } = appPageProps
  useInitLegacyDesignSystemAfterRehydration()

  const siteName = pageProps.config?.siteName || undefined

  // Multilanguage sites '/_next/data' calls workaround
  useInterceptNextDataHref({ router, siteName })

  const renderingStrategy = renderingStrategyAbstractFactory.createRenderingStrategy(pageProps, reactPageRegistry, reactHybridPageRegistry)
  return renderingStrategy.renderApp({
    Component,
    router,
    ReactPage,
    pageProps,
  })
}
